import { Link } from 'react-router-dom'
import { usePageTitle } from '../lib/usePageTitle'

const CONSOLE_URL = 'https://app-bg.planesailing.io'

interface Plan {
  name: string
  eyebrow: string
  price: string
  cadence: string
  summary: string
  features: { label: string; included: boolean }[]
  cta: string
  ctaHref: string
  featured?: boolean
}

const plans: Plan[] = [
  {
    name: 'Open source, self-hosted',
    eyebrow: 'Free · Apache-2.0',
    price: '£0',
    cadence: 'forever',
    summary:
      'Run the whole thing yourself on Cloudflare Workers + D1. Your relay, your identity provider, your data — zero plaintext leaves your edge.',
    features: [
      { label: 'Full ceremony engine: seal, prove, unseal', included: true },
      { label: 'Shamir split + per-recipient public-key encryption', included: true },
      { label: 'Bring your own OIDC identity provider', included: true },
      { label: 'Deploy on Cloudflare Workers + D1', included: true },
      { label: 'Tamper-evident audit log', included: true },
      { label: 'Community support', included: true },
    ],
    cta: 'Self-host on GitHub',
    ctaHref: 'https://github.com/planesailing/breakglass',
    featured: true,
  },
  {
    name: 'Cloud hosted',
    eyebrow: 'Managed',
    price: 'Talk to us',
    cadence: 'per org',
    summary:
      'We run the relay and operate the edge for you. Same zero-plaintext guarantees — the server still only ever holds opaque ciphertext — with managed uptime and onboarding.',
    features: [
      { label: 'Everything in the open-source build', included: true },
      { label: 'Fully managed relay and edge', included: true },
      { label: 'Managed upgrades and backups of ciphertext + metadata', included: true },
      { label: 'Guided quorum onboarding', included: true },
      { label: 'Priority support and SLAs', included: true },
      { label: 'Self-hosting required', included: false },
    ],
    cta: 'Launch console',
    ctaHref: CONSOLE_URL,
  },
]

type Cell = string | boolean

const comparisonRows: { label: string; values: [Cell, Cell] }[] = [
  { label: 'Price', values: ['Free, forever', 'Talk to us'] },
  { label: 'Ceremony engine (seal / prove / unseal)', values: [true, true] },
  { label: 'Shamir split + envelope encryption', values: [true, true] },
  { label: 'Server only ever holds ciphertext', values: [true, true] },
  { label: 'Bring your own OIDC', values: [true, true] },
  { label: 'You operate the infrastructure', values: [true, false] },
  { label: 'Managed relay, edge & backups', values: [false, true] },
  { label: 'Priority support & SLAs', values: [false, true] },
]

function ComparisonCell({ value }: { value: Cell }) {
  if (typeof value === 'string') return <>{value}</>
  if (value) return <span className="check" aria-label="Included">✓</span>
  return <span className="dash" aria-label="Not included">−</span>
}

export function PricingPage() {
  usePageTitle('Pricing')

  return (
    <div className="page">
      <section className="page-hero narrow reveal">
        <div className="hero-pill">Pricing</div>
        <h1>Run it yourself for free. Let us run it when you&apos;d rather not.</h1>
        <p className="hero-lead">
          Break Glass is open source first. Self-host the entire ceremony orchestrator on Cloudflare
          at no cost — or hand the operations to us. Either way, no plaintext ever traverses the
          server.
        </p>
      </section>

      <section className="content-section">
        <div className="pricing-grid">
          {plans.map((plan) => (
            <article className={`price-card reveal${plan.featured ? ' featured' : ''}`} key={plan.name}>
              <div className="tiny-pill">{plan.eyebrow}</div>
              <h2>{plan.name}</h2>
              <p className="price-amount">
                {plan.price}
                <span>{plan.cadence}</span>
              </p>
              <p className="muted">{plan.summary}</p>
              <div className="plan-features">
                {plan.features.map((feature) => (
                  <div key={feature.label} className={feature.included ? 'included' : 'excluded'}>
                    {feature.included ? '✓' : '−'} {feature.label}
                  </div>
                ))}
              </div>
              <a className={`button ${plan.featured ? 'button-solid' : 'button-ghost'}`} href={plan.ctaHref}>
                {plan.cta}
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section content-band">
        <div className="section-heading reveal">
          <div className="eyebrow">Compare the options</div>
          <h2>Same cryptography. You choose who runs it.</h2>
          <p className="muted">
            The hosted service is the open-source build, operated for you. The security model is
            identical — the difference is who keeps the lights on.
          </p>
        </div>
        <div className="table-wrap reveal">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Open source<br />(self-hosted)</th>
                <th>Cloud hosted</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row) => (
                <tr key={row.label}>
                  <td>
                    <div className="comparison-label">
                      <strong>{row.label}</strong>
                    </div>
                  </td>
                  {row.values.map((v, i) => (
                    <td key={i}>
                      <ComparisonCell value={v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="content-section">
        <div className="signup-cta reveal">
          <div>
            <div className="eyebrow">Have questions?</div>
            <h2>Read the feature set and roadmap.</h2>
            <p className="muted">
              What&apos;s in the box today, and what&apos;s coming next — including external-backend
              storage.
            </p>
          </div>
          <div className="cta-actions">
            <Link className="button button-solid" to="/features">
              Browse features →
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
